import { parseZonedDateTime } from '@internationalized/date';
import { differenceInMilliseconds } from 'date-fns';
import { getAircraftItem } from '@lp/features/RoutePlanner/utils/map';
import {
  isPrimaryHospital,
  isPrimaryTxCenter,
  PrimaryLocationIds,
} from '@lp/features/RoutePlanner/utils/case-locations';
import {
  MAX_LEG_DRIVE_DURATION,
  REFUEL_DURATION,
  SEGMENT_END_TIME_OFFSET,
  SWITCH_DURATION,
  SWITCH_DURATION_LOWER,
} from '@lp/features/RoutePlanner/config';
import { CaseResourceType, Randomization, RandomizationResultType } from '@/api/apis/case';
import { HOUR_IN_MS } from '@/constants/date.constants';
import { getTimeDifference, getTzDateTimeString } from '@/utils/helpers';
import {
  AccommodationLeg,
  AircraftLogisticsResourceType,
  AviaLegTypes,
  GroundLegTypes,
  LegLocationTypeEnum,
  RouteLegTypeEnum,
  SegmentTypeEnum,
} from '@/api/apis/route';
import { RouteSegmentType } from '@/types/route';

type ValidateMainSegmentsTimeConflictsProps = {
  legs: (AviaLegTypes | GroundLegTypes)[];
  donorOrTime: string;
  afterOperationTime: string;
  isCaseWithoutTx: boolean;
  isReallocationCase: boolean;
  donorHospitalId?: string;
};

export const validateMainSegmentsTimeConflicts = ({
  legs,
  donorOrTime,
  afterOperationTime,
  isCaseWithoutTx,
  isReallocationCase,
  donorHospitalId,
}: ValidateMainSegmentsTimeConflictsProps) => {
  const toDonorLeg = legs.find(
    (l) => l.segmentType === SegmentTypeEnum.hospital && isPrimaryHospital(l.arrivalLocation, { donorHospitalId }),
  );
  const legEndTime = !!toDonorLeg && parseZonedDateTime(getTzDateTimeString(toDonorLeg.arrivalTime)).toAbsoluteString();
  const isToDonorCorrectTime =
    !toDonorLeg ||
    (!!legEndTime && differenceInMilliseconds(new Date(donorOrTime), new Date(legEndTime)) >= HOUR_IN_MS);

  const fromDonorLeg = legs.find(
    (l) => l.segmentType === SegmentTypeEnum.txcenter && isPrimaryHospital(l.departureLocation, { donorHospitalId }),
  );
  const legStartTime =
    !!fromDonorLeg && parseZonedDateTime(getTzDateTimeString(fromDonorLeg.departureTime)).toAbsoluteString();
  const isFromDonorCorrectTime =
    !fromDonorLeg ||
    (!!legStartTime && differenceInMilliseconds(new Date(legStartTime), new Date(afterOperationTime)) >= 0);

  const hasTimeConflicts = legs
    .filter((l) =>
      [SegmentTypeEnum.hospital, SegmentTypeEnum.txcenter, SegmentTypeEnum.hub, SegmentTypeEnum.dhToHub].includes(
        l.segmentType,
      ),
    )
    .reduce((acc, leg, index) => {
      if (!index) return false;
      if (acc) return acc;

      const currentLegStartTime = parseZonedDateTime(getTzDateTimeString(leg.departureTime)).toAbsoluteString();
      const prevLeg = legs[index - 1];
      const prevLegEndTime = parseZonedDateTime(getTzDateTimeString(prevLeg.arrivalTime)).toAbsoluteString();

      return differenceInMilliseconds(new Date(prevLegEndTime), new Date(currentLegStartTime)) > 0;
    }, false);

  if (isCaseWithoutTx) {
    return isToDonorCorrectTime && !hasTimeConflicts;
  }

  if (isReallocationCase) {
    return isFromDonorCorrectTime && !hasTimeConflicts;
  }

  return isToDonorCorrectTime && isFromDonorCorrectTime && !hasTimeConflicts;
};

export const validateDryRunSegmentsTimeConflicts = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  donorOrTime: string,
  donorHospitalId?: string,
) => {
  const fromDonorLeg = legs.find(
    (l) => l.segmentType === SegmentTypeEnum.dhToHub && isPrimaryHospital(l.departureLocation, { donorHospitalId }),
  );
  const legStartTime =
    !!fromDonorLeg && parseZonedDateTime(getTzDateTimeString(fromDonorLeg.departureTime)).toAbsoluteString();
  const isDryRunTimeValid =
    !!legStartTime && differenceInMilliseconds(new Date(legStartTime), new Date(donorOrTime)) >= 0;

  return !fromDonorLeg || isDryRunTimeValid;
};

export const validateHospitalSegmentLegs = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  primary: PrimaryLocationIds = {},
) => {
  const hospitalSegmentLegs = legs.filter((l) => l.segmentType === SegmentTypeEnum.hospital);
  const hasHubLeg = hospitalSegmentLegs.some((l) => l.departureLocation.locationType === LegLocationTypeEnum.hub);
  const hasHospitalLeg = hospitalSegmentLegs.some((l) => isPrimaryHospital(l.arrivalLocation, primary));

  return hasHospitalLeg && hasHubLeg;
};

export const validateTxCenterSegmentLegs = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  primary: PrimaryLocationIds = {},
  isReallocation: boolean = false,
) => {
  const txCenterSegmentLegs = legs.filter((l) => l.segmentType === SegmentTypeEnum.txcenter);
  const hasTxCenterLeg = txCenterSegmentLegs.some((l) => isPrimaryTxCenter(l.arrivalLocation, primary));
  const hasHospitalLeg = txCenterSegmentLegs.some((l) => isPrimaryHospital(l.departureLocation, primary));

  if (isReallocation) {
    return hasTxCenterLeg;
  }

  return hasHospitalLeg && hasTxCenterLeg;
};

export const validateReturnSegmentLegs = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  segmentType: SegmentTypeEnum,
  primary: PrimaryLocationIds = {},
) => {
  const returnSegmentLegs = legs.filter((l) => l.segmentType === segmentType);
  const hasHubLeg = returnSegmentLegs.some((l) => l.arrivalLocation.locationType === LegLocationTypeEnum.hub);
  const hasTxCenterLeg = returnSegmentLegs.some((l) => isPrimaryTxCenter(l.departureLocation, primary));
  const hasHospitalLeg = returnSegmentLegs.some((l) => isPrimaryHospital(l.departureLocation, primary));
  const hasStartLeg = segmentType === SegmentTypeEnum.dhToHub ? hasHospitalLeg : hasTxCenterLeg;

  return hasHubLeg && hasStartLeg;
};

type ValidateMainRouteProps = {
  legs: (AviaLegTypes | GroundLegTypes)[];
  opoRequest: boolean;
  expeditedCase: boolean;
  reAllocationCase: boolean;
  randomization?: Randomization;
  primary: PrimaryLocationIds;
};

export const validateMainRoute = ({
  legs,
  opoRequest,
  expeditedCase,
  reAllocationCase,
  randomization,
  primary,
}: ValidateMainRouteProps): { isValid: boolean; errorMessage: string } => {
  const isSCS = randomization?.result === RandomizationResultType.SCS;
  const isHospitalSegmentValid = validateHospitalSegmentLegs(legs, primary);
  const isTxCenterSegmentValid = validateTxCenterSegmentLegs(legs, primary, reAllocationCase);
  const isReturnSegmentValid = isSCS || validateReturnSegmentLegs(legs, SegmentTypeEnum.hub, primary);

  if (opoRequest || expeditedCase) {
    return {
      isValid: isHospitalSegmentValid,
      errorMessage: !isHospitalSegmentValid
        ? `Hub-DH segment has to be filled in for ${opoRequest ? 'OPO request' : 'expedited'} case`
        : '',
    };
  }

  if (reAllocationCase) {
    const isValid = isTxCenterSegmentValid && isReturnSegmentValid;

    return {
      isValid,
      errorMessage: !isValid ? 'DH-TXC-Hub segments have to be filled in for Re-allocation case' : '',
    };
  }

  const isValid = isHospitalSegmentValid && isTxCenterSegmentValid && isReturnSegmentValid;

  return {
    isValid,
    errorMessage: !isValid ? 'Hub-DH-TXC-Hub segments have to be filled in for the case' : '',
  };
};

export const validateDryRunRoute = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  primary: PrimaryLocationIds,
): { isValid: boolean; errorMessage: string } => {
  const isReturnSegmentValid = validateReturnSegmentLegs(legs, SegmentTypeEnum.dhToHub, primary);

  return {
    isValid: isReturnSegmentValid,
    errorMessage: !isReturnSegmentValid ? 'DH-Hub segments have to be filled in for the case' : '',
  };
};

export const validateDeviceInMainSegments = (legs: (AviaLegTypes | GroundLegTypes)[], isDryRun: boolean = false) => {
  const segmentsList = isDryRun
    ? [SegmentTypeEnum.dhToHub]
    : [SegmentTypeEnum.hospital, SegmentTypeEnum.txcenter, SegmentTypeEnum.hub];
  const mainLegs = legs.filter((l) => segmentsList.includes(l.segmentType));

  return mainLegs.every((l) => l.resources.some((r) => r.resourceType === CaseResourceType.OCSConsole));
};

export const validateAccommodationLegs = (legs: AccommodationLeg[]) => {
  return legs.every((l) => !!l.hotelName && !!l.location?.locationId && !!l.rooms);
};

export const validateTmdxAvia = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  aircrafts: AircraftLogisticsResourceType[],
): { isValid: boolean; errorMessage: string } => {
  if (!aircrafts?.length) {
    return {
      isValid: true,
      errorMessage: '',
    };
  }

  const legsWithAvia = legs
    .filter((l) => l.legType === RouteLegTypeEnum.TmdxJetAviaLeg)
    .sort((a, b) => (differenceInMilliseconds(a.departureTime, b.arrivalTime) > 0 ? 1 : -1));

  const aircraftsStartFromDiffPlace = aircrafts.filter((a) => {
    if (!a.data) return false;

    const aircraftStartLeg = legsWithAvia.find((l) =>
      l.resources.some((r) => r.resourceType === CaseResourceType.Aircraft && r.resourceId === a.resourceId),
    );

    if (!aircraftStartLeg) return false;

    const aircraftItem = getAircraftItem({
      aircraft: a.data,
      filters: { from: aircraftStartLeg.departureTime, to: aircraftStartLeg.arrivalTime },
    });
    const currentAirportId = aircraftItem.availabilities[0]?.airport.id;

    if (!currentAirportId) {
      return true;
    }

    return aircraftStartLeg.departureLocation.locationId !== currentAirportId;
  });

  const repositionAircaftsList = aircraftsStartFromDiffPlace.map((a) => a.data?.registration);

  return {
    isValid: !repositionAircaftsList.length,
    errorMessage: repositionAircaftsList.length
      ? `Aircraft ${repositionAircaftsList.join(', ')} may require reposition`
      : '',
  };
};

// Primary segment endpoints which must point to the case DH/TXC: the first departure and the final arrival of
// the main segments. Other DH/TXC locations (custom segments, intermediate stops like the previous TXC of
// a reallocation case) are allowed and not validated.
const PRIMARY_DEPARTURE_SEGMENTS: Partial<Record<LegLocationTypeEnum, SegmentTypeEnum[]>> = {
  [LegLocationTypeEnum.hospital]: [SegmentTypeEnum.txcenter, SegmentTypeEnum.dhToHub],
  [LegLocationTypeEnum.txcenter]: [SegmentTypeEnum.hub],
};

const PRIMARY_ARRIVAL_SEGMENTS: Partial<Record<LegLocationTypeEnum, SegmentTypeEnum[]>> = {
  [LegLocationTypeEnum.hospital]: [SegmentTypeEnum.hospital],
  [LegLocationTypeEnum.txcenter]: [SegmentTypeEnum.txcenter],
};

export const validateLocation = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  locationType: LegLocationTypeEnum.hospital | LegLocationTypeEnum.txcenter,
  locationId: string,
) => {
  const isValidEndpoint = (location?: { locationType: LegLocationTypeEnum; locationId: string }) =>
    !location || location.locationType !== locationType || location.locationId === locationId;

  const hasValidDepartures = (PRIMARY_DEPARTURE_SEGMENTS[locationType] || []).every((segmentType) =>
    isValidEndpoint(legs.find((l) => l.segmentType === segmentType)?.departureLocation),
  );
  const hasValidArrivals = (PRIMARY_ARRIVAL_SEGMENTS[locationType] || []).every((segmentType) =>
    isValidEndpoint(legs.findLast((l) => l.segmentType === segmentType)?.arrivalLocation),
  );

  return hasValidDepartures && hasValidArrivals;
};

export const validateLegTimeConflicts = (
  segment: RouteSegmentType,
  leg: AviaLegTypes | GroundLegTypes,
  prevLeg?: AviaLegTypes | GroundLegTypes,
): string[] => {
  let warnings: string[] = [];
  const legStartTime = parseZonedDateTime(getTzDateTimeString(leg.departureTime)).toAbsoluteString();
  const legEndTime = parseZonedDateTime(getTzDateTimeString(leg.arrivalTime)).toAbsoluteString();
  const prevLegEndTime =
    prevLeg?.arrivalTime && parseZonedDateTime(getTzDateTimeString(prevLeg.arrivalTime)).toAbsoluteString();
  const segmentStartTime =
    segment.startTime && parseZonedDateTime(getTzDateTimeString(segment.startTime)).toAbsoluteString();
  const segmentEndTime = segment.endTime && parseZonedDateTime(getTzDateTimeString(segment.endTime)).toAbsoluteString();

  if (segment.type === SegmentTypeEnum.hospital) {
    const isSegmentEndConflict =
      !!segmentEndTime && differenceInMilliseconds(segmentEndTime, legEndTime) < SEGMENT_END_TIME_OFFSET;

    if (isSegmentEndConflict) {
      warnings = [...warnings, "The leg's arrival time is more than OR-1h"];
    }
  }

  if (segment.type === SegmentTypeEnum.hub) {
    const isSegmentStartConflict = !!segmentStartTime && differenceInMilliseconds(segmentStartTime, legStartTime) > 0;

    if (isSegmentStartConflict) {
      warnings = [...warnings, "The leg's departure time is less than TX ETA+1h"];
    }
  }

  if (segment.type === SegmentTypeEnum.txcenter) {
    const isSegmentStartConflict = !!segmentStartTime && differenceInMilliseconds(segmentStartTime, legStartTime) > 0;

    if (isSegmentStartConflict) {
      warnings = [...warnings, "The leg's departure time is less than OR+operation time"];
    }
  }

  if (segment.type === SegmentTypeEnum.dhToHub) {
    const isSegmentStartConflict = !!segmentStartTime && differenceInMilliseconds(segmentStartTime, legStartTime) > 0;

    if (isSegmentStartConflict) {
      warnings = [...warnings, "The leg's departure time is less than OR time"];
    }
  }

  if (prevLeg && prevLeg.arrivalLocation.locationId !== leg.departureLocation.locationId) {
    warnings = [...warnings, 'The route legs are not connected'];
  }

  const currentAircraft = leg.resources.find((r) => r.resourceType === CaseResourceType.Aircraft);
  const prevAircraft = prevLeg?.resources.find((r) => r.resourceType === CaseResourceType.Aircraft);

  if (currentAircraft && prevAircraft && prevAircraft.resourceId === currentAircraft.resourceId) {
    const isRefuelConflict =
      !!prevLegEndTime && differenceInMilliseconds(legStartTime, prevLegEndTime) < REFUEL_DURATION;

    if (isRefuelConflict) {
      warnings = [...warnings, 'The plane refuel stop is less than 1h.'];
    }
  }

  if (
    prevLeg &&
    [LegLocationTypeEnum.hospital, LegLocationTypeEnum.txcenter, LegLocationTypeEnum.airport].includes(
      prevLeg.arrivalLocation.locationType,
    )
  ) {
    const switchDuration =
      prevLeg.arrivalLocation.locationType === LegLocationTypeEnum.airport ? SWITCH_DURATION : SWITCH_DURATION_LOWER;
    const isBufferAccepted =
      !!prevLegEndTime && differenceInMilliseconds(legStartTime, prevLegEndTime) >= switchDuration;

    if (!isBufferAccepted) {
      warnings = [...warnings, 'The time difference less than corresponding Car Loading/Unloading buffer.'];
    }
  }

  if (prevLegEndTime) {
    const isLegsTimeOverlap = differenceInMilliseconds(prevLegEndTime, legStartTime) > 0;

    if (isLegsTimeOverlap) {
      warnings = [...warnings, 'The departure and arrival time of legs conflicts and has to be updated.'];
    }
  }

  if (
    leg.legType === RouteLegTypeEnum.TmdxAffiliateGroundLeg ||
    leg.legType === RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg
  ) {
    const legDuration = getTimeDifference(new Date(leg.departureTime), new Date(leg.arrivalTime));

    if ((legDuration.MILLISECONDS || 0) > MAX_LEG_DRIVE_DURATION) {
      warnings = [...warnings, 'Actual drive time is more than 3 hours.'];
    }
  }

  return warnings;
};
